import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  message,
  Modal,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  MessageOutlined,
  PaperClipOutlined,
  ProfileOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  ShareAltOutlined,
  StopOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import { hasRichText, richTextMarkup } from "./richTextUtils";
import "./JobApplicationDetails.css";

const { Paragraph, Text, Title } = Typography;
const { TextArea } = Input;

const INTERVIEW_TYPE_OPTIONS = ["TECHNICAL", "NONTECHNICAL"];
const INTERVIEW_MODE_OPTIONS = ["VIDEO", "PHONE", "INPERSON", "ASSIGNMENT"];
const INTERVIEW_STATUS_OPTIONS = ["SCHEDULED", "CANCELLED", "COMPLETED"];
const INTERVIEW_FEEDBACK_OPTIONS = ["NONE", "PENDING", "PASSED", "FAILED"];

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const titleCase = (value) =>
  String(value || "")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const selectOptions = (values) =>
  values.map((value) => ({
    label: titleCase(value),
    value,
  }));

const formatDateTime = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const toDatetimeLocalValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const fromDatetimeLocalValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
};

const pageContent = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const applicationTitle = (application) =>
  firstText(
    application?.jobOpportunity?.title,
    application?.jobOpportunityTitle,
    application?.jobTitle,
    application?.title,
  ) || "Job Application";

const applicationJobId = (application) =>
  firstText(application?.jobOpportunityId, application?.jobOpportunity?.id, application?.jobPost?.id);

const snapshotEntryValue = (application, ...keys) =>
  firstText(application?.professionProfileSnapshot?.find((entry) => keys.includes(entry.key))?.value);

const applicantName = (application) => {
  const applicant = application?.applicant || application?.user || application?.applicantProfile || {};
  return firstText(
    applicant.fullName,
    applicant.displayName,
    applicant.name,
    [applicant.firstName, applicant.lastName].filter(Boolean).join(" "),
    application?.applicantName,
    application?.userName,
    snapshotEntryValue(application, "userDetails.name", "profileName", "profile.profileName"),
  ) || "Applicant";
};

const isApplicationApplicant = (application, userId) => {
  const applicantId = firstText(application?.applicantId, application?.applicant?.id, application?.user?.id);
  return Boolean(applicantId && userId && applicantId === userId);
};

const isPosterApplication = (application, userId, workspaceId) => {
  const jobPost = application?.jobOpportunity || application?.jobPost || {};
  const posterId = firstText(jobPost.posterId, jobPost.poster?.id, application?.posterId);
  const itemWorkspaceId = firstText(jobPost.workspaceId, jobPost.workspace?.id, application?.workspaceId);
  return Boolean((posterId && userId && posterId === userId) || (itemWorkspaceId && workspaceId && itemWorkspaceId === workspaceId));
};

const statusTone = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "SUBMITTED" || normalized === "NEW" || normalized === "APPLIED") return "submitted";
  if (normalized === "REJECTED" || normalized === "REJECTED_CLOSED" || normalized === "CANCELLED" || normalized === "CANCELLED_CLOSED") return "rejected";
  if (normalized === "HIRED" || normalized === "HIRED_CLOSED") return "hired";
  return "active";
};

const parseApplicationNote = (value) => {
  const note = String(value || "").trim();
  const marker = "Application question answers:";
  const markerIndex = note.indexOf(marker);
  if (markerIndex < 0) return { intro: note, answers: [] };

  const intro = note.slice(0, markerIndex).trim();
  const answersText = note.slice(markerIndex + marker.length).trim();
  const answers = answersText
    .split(/\n{2,}/)
    .map((block) => {
      const question = firstText(block.match(/^Q:\s*(.+)$/m)?.[1]);
      const metric = firstText(block.match(/^Metric:\s*(.+)$/m)?.[1]);
      const answer = firstText(block.match(/^A:\s*([\s\S]+)$/m)?.[1]);
      return question && answer ? { question, metric, answer } : null;
    })
    .filter(Boolean);
  return { intro, answers };
};

const statusUpdates = (application) =>
  pageContent(
    application?.statusUpdates ||
      application?.statusHistory ||
      application?.history ||
      application?.jobApplicationStatusUpdates,
  );

const historyTimestamp = (item) => {
  const value = firstText(item?.addedAt, item?.createdAt, item?.updatedAt, item?.date, item?.timestamp);
  const time = value ? new Date(value).getTime() : 0;
  return Number.isFinite(time) ? time : 0;
};

const ATTACHMENT_SNAPSHOT_SECTIONS = ["documents", "photos", "certifications", "licenses"];

const compactTextList = (values = []) =>
  values
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .map((value) => {
      if (typeof value === "string") return value.trim();
      if (typeof value === "number") return String(value);
      if (value && typeof value === "object") {
        return firstText(value.name, value.title, value.label, value.code, value.description, value.city);
      }
      return "";
    })
    .filter(Boolean);

const tryParseSnapshotValue = (value) => {
  const text = String(value || "").trim();
  if (!text || (!text.startsWith("{") && !text.startsWith("["))) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const isOpenableFileUri = (value) => /^(https?:|file:|content:|ph:)/i.test(String(value || "").trim());

const fileUriFromValue = (value) => {
  if (typeof value === "string") return isOpenableFileUri(value) ? value.trim() : "";
  if (!value || typeof value !== "object") return "";
  return firstText(
    value.signedUrl,
    value.signedURL,
    value.downloadUrl,
    value.downloadURL,
    value.fileUrl,
    value.fileURL,
    value.url,
    value.uri,
    value.localUri,
    value.fileLocalUri,
    isOpenableFileUri(value.fileId) ? value.fileId : "",
    isOpenableFileUri(value.id) ? value.id : "",
  );
};

const fileIdFromValue = (value) => {
  if (typeof value === "string") return isOpenableFileUri(value) ? "" : value;
  if (!value || typeof value !== "object") return "";
  return firstText(value.id, value.fileId, value.uuid, value.documentId, value.photoId);
};

const attachmentDisplayParts = (sectionKey, value, index = 0) => {
  const data = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const fileUri = fileUriFromValue(value);
  const fileId = fileIdFromValue(value);
  const hasAttachment = Boolean(fileUri || fileId);
  if (sectionKey === "photos") {
    return {
      title: firstText(data.caption, data.name, data.fileName, data.title, `Photo ${index + 1}`),
      subtitle: fileUri ? "Photo preview available" : fileId ? "Photo attached" : "",
      meta: firstText(data.uploadedAt, data.createdAt, data.mimeType, data.contentType),
      fileUri,
      hasAttachment,
    };
  }
  if (sectionKey === "certifications") {
    return {
      title: firstText(data.name, data.title, data.certificationName, `Certification ${index + 1}`),
      subtitle: compactTextList([data.issuer, data.issuingOrganization, data.certificationNumber]).join(" • "),
      meta: compactTextList([data.issueDate, data.issuedAt, data.expiryDate, data.expiresAt, fileId && !fileUri ? "Certificate attached" : ""]).join(" • "),
      fileUri,
      hasAttachment,
    };
  }
  if (sectionKey === "licenses") {
    return {
      title: firstText(data.name, data.title, data.licenseName, `License ${index + 1}`),
      subtitle: compactTextList([data.issuer, data.issuingOrganization, data.certificationNumber, data.licenseNumber]).join(" • "),
      meta: compactTextList([data.issueDate, data.issuedAt, data.expiryDate, data.expiresAt, fileId && !fileUri ? "License attached" : ""]).join(" • "),
      fileUri,
      hasAttachment,
    };
  }
  return {
    title: firstText(data.caption, data.name, data.fileName, data.documentName, data.type, `Document ${index + 1}`),
    subtitle: firstText(data.description, data.documentType, data.mimeType, data.contentType, fileUri ? "Document preview available" : ""),
    meta: firstText(data.status, data.uploadedAt, data.createdAt, fileId && !fileUri ? "Document attached" : ""),
    fileUri,
    hasAttachment,
  };
};

const snapshotAttachmentDisplay = (item, index = 0) => {
  if (!ATTACHMENT_SNAPSHOT_SECTIONS.includes(item?.sectionKey)) return null;
  const parsed = tryParseSnapshotValue(item.value);
  const value = Array.isArray(parsed) ? parsed[0] : parsed || item.value;
  const display = attachmentDisplayParts(item.sectionKey, value, index);
  return display.hasAttachment ? display : null;
};

const displaySnapshotValue = (value) => {
  const text = String(value || "").trim();
  if (!text) return "Not set";
  if (!text.startsWith("{") && !text.startsWith("[")) return text;
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed.map(displaySnapshotValue).filter(Boolean).join(", ");
    if (parsed && typeof parsed === "object") {
      return Object.entries(parsed)
        .filter(([, nextValue]) => nextValue != null && String(nextValue).trim())
        .map(([key, nextValue]) => `${titleCase(key)}: ${displaySnapshotValue(nextValue)}`)
        .join(" | ");
    }
  } catch {
    return text;
  }
  return text;
};

const sharedLabel = (value) => (value ? "Yes" : "No");

const asList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (value instanceof Set) return Array.from(value);
  return [value];
};

const interviewersInputValue = (value) => {
  if (Array.isArray(value)) return value.map(namedLabel).filter(Boolean).join(", ");
  if (typeof value === "string") return value;
  return "";
};

const interviewersPayloadValue = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const numericValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const clampOrderNumber = (value) => Math.max(1, Math.min(50, Math.round(numericValue(value) || 1)));

const sessionTimeValue = (session) => {
  const time = session?.interviewDateTime ? new Date(session.interviewDateTime).getTime() : 0;
  return Number.isFinite(time) && time > 0 ? time : Number.MAX_SAFE_INTEGER;
};

const deriveInterviewOrderNumber = (sessions, currentSession, title, interviewDateTime) => {
  const draft = {
    ...(currentSession || {}),
    title,
    interviewDateTime,
  };
  const currentId = currentSession?.id;
  const nextSessions = [
    ...sessions.filter((session) => !currentId || session.id !== currentId),
    draft,
  ].sort((left, right) => {
    const byTime = sessionTimeValue(left) - sessionTimeValue(right);
    if (byTime !== 0) return byTime;
    return String(left.title || "").localeCompare(String(right.title || ""));
  });
  const index = nextSessions.findIndex((session) => session === draft || (currentId && session.id === currentId));
  return clampOrderNumber(index >= 0 ? index + 1 : nextSessions.length || 1);
};

const interviewFormInitialValues = (session) => ({
  title: session?.title || "",
  type: session?.type || "TECHNICAL",
  interviewMode: session?.interviewMode || "VIDEO",
  interviewDateTime: toDatetimeLocalValue(session?.interviewDateTime),
  durationMinutes: session?.durationMinutes ?? session?.duration ?? 30,
  description: session?.description || "",
  meetingLink: session?.meetingLink || session?.metingLink || "",
  location: session?.location || "",
  status: session?.status || "SCHEDULED",
  interviewFeedbackStatus: session?.interviewFeedbackStatus || "NONE",
  interviewFeedbackInternal: session?.interviewFeedbackInternal || "",
  interviewFeedbackToCandidate: session?.interviewFeedbackToCandidate || "",
  interviewers: interviewersInputValue(session?.interviewers),
  notesToCandidate: session?.notesToCandidate || "",
});

const interviewSessionPayload = (values, sessions, currentSession) => {
  const title = String(values.title || "").trim();
  const interviewDateTime = fromDatetimeLocalValue(values.interviewDateTime);
  const durationMinutes = numericValue(values.durationMinutes);
  return {
    title,
    type: values.type || "TECHNICAL",
    interviewMode: values.interviewMode || "VIDEO",
    interviewDateTime,
    durationMinutes: durationMinutes ? Math.max(1, Math.round(durationMinutes)) : null,
    description: String(values.description || "").trim() || null,
    meetingLink: String(values.meetingLink || "").trim() || null,
    location: String(values.location || "").trim() || null,
    status: values.status || "SCHEDULED",
    interviewFeedbackStatus: values.interviewFeedbackStatus || "NONE",
    interviewFeedbackInternal: String(values.interviewFeedbackInternal || "").trim() || null,
    interviewFeedbackToCandidate: String(values.interviewFeedbackToCandidate || "").trim() || null,
    interviewers: interviewersPayloadValue(values.interviewers),
    notesToCandidate: String(values.notesToCandidate || "").trim() || null,
    orderNumber: deriveInterviewOrderNumber(sessions, currentSession, title, interviewDateTime),
  };
};

const formatDuration = (value) => {
  const minutes = numericValue(value);
  if (!minutes) return "";
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

const interviewFeedbackLabel = (value) => {
  const normalized = String(value || "NONE").toUpperCase();
  if (normalized === "NONE") return "Feedback: Not started";
  return `Feedback: ${titleCase(normalized)}`;
};

const isRawReferenceId = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || "").trim());

const displayReferenceValue = (value) => {
  const text = firstText(value);
  return text && !isRawReferenceId(text) ? text : "";
};

const interviewersDisplayValue = (value) => {
  const labels = asList(value)
    .map((item) => {
      if (typeof item === "string" && isRawReferenceId(item)) return "";
      return namedLabel(item);
    })
    .filter((label) => label && !isRawReferenceId(label));
  return labels.join(", ");
};

const interviewSessionDetailItems = (session) =>
  [
    { label: "Location", value: displayReferenceValue(session?.location) || "Location not set" },
    {
      label: "Meeting link",
      value: session?.meetingLink || session?.metingLink || "Not set",
      link: Boolean(session?.meetingLink || session?.metingLink),
    },
    { label: "Interviewers", value: interviewersDisplayValue(session?.interviewers) || "Not set" },
    { label: "Created", value: session?.createdAt ? formatDateTime(session.createdAt) : "" },
    { label: "Updated", value: session?.updatedAt ? formatDateTime(session.updatedAt) : "" },
  ].filter((item) => item.value);

const interviewSessionTextSections = (session, canManageApplication) =>
  [
    { label: "Description", value: session?.description },
    { label: "Notes to candidate", value: session?.notesToCandidate },
    { label: "Candidate feedback", value: session?.interviewFeedbackToCandidate },
    canManageApplication
      ? { label: "Internal feedback", value: session?.interviewFeedbackInternal }
      : null,
  ].filter((item) => item?.value);

const namedLabel = (value) => {
  if (!value) return "";
  if (typeof value === "string") return titleCase(value);
  return firstText(value.name, value.label, value.title, value.code);
};

const normalizeLabels = (value) => asList(value).map(namedLabel).filter(Boolean);

const jobField = (job, ...keys) => firstText(...keys.map((key) => job?.[key]));

const embeddedJobPost = (application) => application?.jobOpportunity || application?.jobPost || null;

const JobPostDrawer = ({ job, open, loading, onClose }) => {
  const schedules = normalizeLabels(job?.workSchedules);
  const skills = normalizeLabels(job?.skills || job?.requiredSkills);
  const specializations = normalizeLabels(job?.specializations || job?.requiredSpecializations);
  const locations = normalizeLabels(job?.requiredLocations);
  const detailItems = [
    { label: "Status", value: titleCase(job?.status || "Open") },
    { label: "Posted", value: formatDateTime(job?.createdAt) },
    { label: "Application window", value: `${formatDateTime(job?.postStartDate)} - ${formatDateTime(job?.postEndDate)}` },
    { label: "Location type", value: titleCase(job?.locationType) },
    { label: "Schedule", value: schedules.join(", ") },
    {
      label: "Experience",
      value: job?.requiredYearsOfExperience != null ? `${job.requiredYearsOfExperience}+ years` : "",
    },
    { label: "Education", value: titleCase(job?.requiredEducationLevel) },
    {
      label: "Salary",
      value: jobField(job, "salaryExpectation", "salaryExpectations", "salaryRange", "compensation", "pay", "payRange"),
    },
  ].filter((item) => item.value);
  const sections = [
    { label: "Responsibilities", value: job?.responsibilities },
    { label: "Qualifications", value: job?.qualifications },
    { label: "Requirements", value: job?.requirements },
    { label: "Benefits", value: job?.benefits },
    { label: "How to apply", value: jobField(job, "applicationInstructions", "howToApply", "applyInstructions") },
  ].filter((item) => item.value);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={job?.title || "Job post"}
      width="min(540px, 100vw)"
      className="job-application-job-drawer"
    >
      {loading && !job ? (
        <Skeleton active />
      ) : !job ? (
        <Empty description="Job post details are not available." />
      ) : (
        <Space direction="vertical" size={16} className="job-application-job-drawer__content">
          {loading ? <Alert type="info" showIcon message="Refreshing job post details..." /> : null}
          <div className="job-application-job-drawer__header">
            <Text type="secondary">Job opportunity</Text>
            <Title level={4}>{job.title || "Untitled job post"}</Title>
            <Space wrap>
              {job.status ? <Tag color="green">{titleCase(job.status)}</Tag> : null}
              {job.locationType ? <Tag>{titleCase(job.locationType)}</Tag> : null}
            </Space>
          </div>

          {hasRichText(job.description) ? (
            <div
              className="job-application-job-drawer__description olreadia-rich-text"
              dangerouslySetInnerHTML={richTextMarkup(job.description)}
            />
          ) : null}

          {detailItems.length ? (
            <div className="job-application-job-drawer__details">
              {detailItems.map((item) => (
                <div key={item.label}>
                  <Text type="secondary">{item.label}</Text>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          ) : null}

          {locations.length ? (
            <div className="job-application-job-drawer__tags">
              <Text type="secondary">Locations</Text>
              <Space wrap>{locations.map((label) => <Tag key={label}>{label}</Tag>)}</Space>
            </div>
          ) : null}

          {skills.length || specializations.length ? (
            <div className="job-application-job-drawer__tags">
              <Text type="secondary">Skills and specializations</Text>
              <Space wrap>{[...skills, ...specializations].map((label) => <Tag key={label}>{label}</Tag>)}</Space>
            </div>
          ) : null}

          {sections.map((section) => (
            <Card key={section.label} className="job-application-job-drawer__section">
              <Text type="secondary">{section.label}</Text>
              <Paragraph>{section.value}</Paragraph>
            </Card>
          ))}
        </Space>
      )}
    </Drawer>
  );
};

const ActionModal = ({ action, value, setValue, loading, onCancel, onSubmit }) => (
  <Modal
    open={Boolean(action)}
    title={<span className="job-application-action-modal__title">{action?.title}</span>}
    className="job-application-action-modal"
    okText={action?.confirmLabel || "Submit"}
    okButtonProps={{
      className: action?.destructive ? "" : "job-application-action-modal__ok",
      danger: Boolean(action?.destructive),
      loading,
    }}
    onCancel={onCancel}
    onOk={onSubmit}
    destroyOnClose
  >
    <Paragraph className="job-application-action-modal__message">{action?.message}</Paragraph>
    <TextArea
      rows={4}
      value={value}
      onChange={(event) => setValue(event.target.value)}
      placeholder={action?.placeholder || "Add a message"}
    />
  </Modal>
);

const HistoryNote = ({ label, value }) => {
  const note = firstText(value);
  if (!note) return null;
  const parts = parseApplicationNote(note);
  const displayNote = parts.intro || (parts.answers.length ? "" : note);
  if (!displayNote) return null;

  return (
    <div className="job-application-details-history-note">
      {label ? <Text className="job-application-details-history-note__label">{label}</Text> : null}
      <Paragraph>{displayNote}</Paragraph>
    </div>
  );
};

const QuestionAnswerList = ({ answers }) => (
  <div className="job-application-details-answer-list">
    {answers.map((item, index) => (
      <Card key={`${item.question}-${item.answer}-${index}`} className="job-application-details-answer-card">
        <Text strong>Q: {item.question}</Text>
        {item.metric ? <Text type="secondary">Metric: {item.metric}</Text> : null}
        <Paragraph>A: {item.answer}</Paragraph>
      </Card>
    ))}
  </div>
);

export default function JobApplicationDetails() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    selectedWorkspaceId,
    selectedWorkspace,
    selectedWorkspaceUse,
    isProfessionalWorkspace,
  } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const [interviewForm] = Form.useForm();

  const [application, setApplication] = useState(null);
  const [interviewSessions, setInterviewSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingInterviews, setLoadingInterviews] = useState(false);
  const [error, setError] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [actionNote, setActionNote] = useState("");
  const [updating, setUpdating] = useState(false);
  const [jobPostDrawerOpen, setJobPostDrawerOpen] = useState(false);
  const [jobPost, setJobPost] = useState(null);
  const [loadingJobPost, setLoadingJobPost] = useState(false);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [interviewEditor, setInterviewEditor] = useState(null);
  const [interviewDrawerSession, setInterviewDrawerSession] = useState(null);
  const [savingInterview, setSavingInterview] = useState(false);

  const normalizedStatus = String(application?.status || "SUBMITTED").toUpperCase();
  const isClosed = ["HIRED_CLOSED", "REJECTED_CLOSED", "CANCELLED_CLOSED"].includes(normalizedStatus);
  const isWithdrawn = normalizedStatus === "WITHDRAWN" || normalizedStatus === "WITHDROWN";
  const isInterviewing = normalizedStatus === "INTERVIEWING";
  const jobOpportunityId = applicationJobId(application);
  const applicationJobPost = useMemo(() => embeddedJobPost(application), [application]);
  const resolvedJobPost = jobPost || applicationJobPost;
  const applicationWithResolvedJob = useMemo(
    () => (application ? { ...application, jobOpportunity: resolvedJobPost || application.jobOpportunity } : application),
    [application, resolvedJobPost],
  );
  const canManageApplication = isPosterApplication(applicationWithResolvedJob, actorId, selectedWorkspaceId) && !isProfessionalWorkspace;
  const canRequestApplicantAction = isApplicationApplicant(application, actorId) && !canManageApplication && isProfessionalWorkspace;
  const showApplicantActions = canRequestApplicantAction && !isClosed && !isWithdrawn;
  const isInitialApplicationStatus = ["SUBMITTED", "APPLIED", ""].includes(normalizedStatus);
  const isUnderReviewStatus = normalizedStatus === "UNDER_REVIEW";
  const isOfferedStatus = normalizedStatus === "OFFERED";
  const isHiredStatus = normalizedStatus === "HIRED" || normalizedStatus === "HIRED_CLOSED";
  const isRejectedStatus = normalizedStatus === "REJECTED" || normalizedStatus === "REJECTED_CLOSED";
  const closeApplicationActionLabel = isHiredStatus || isRejectedStatus ? "Close Application" : "Cancel Application";
  const noteParts = useMemo(() => parseApplicationNote(application?.note), [application?.note]);
  const history = useMemo(
    () => [...statusUpdates(application)].sort((left, right) => historyTimestamp(right) - historyTimestamp(left)),
    [application],
  );
  const applicationQuestionAnswers = useMemo(() => {
    const seen = new Set();
    return [
      ...parseApplicationNote(application?.note).answers,
      ...history.flatMap((item) => [
        ...parseApplicationNote(item.noteToApplicant).answers,
        ...parseApplicationNote(item.noteToHiringTeam || item.note).answers,
        ...parseApplicationNote(item.internalNote).answers,
      ]),
    ].filter((answer) => {
      const key = `${answer.question}|${answer.metric || ""}|${answer.answer}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [application?.note, history]);
  const promotionItems = useMemo(
    () => [
      {
        eyebrow: "Sponsored",
        slotLabel: "Ad space",
        placeholder: true,
        icon: "rocket",
        pinned: true,
      },
      {
        eyebrow: "Profile strength",
        title: "Keep your profile current",
        titleLevel: 5,
        description:
          "Keep experience, documents, and availability ready for the next review.",
        actionLabel: "Improve profile",
        featured: true,
        icon: "profile",
        onAction: () => navigate(paths.professionalProfiles),
      },
      {
        eyebrow: "Background checks",
        title: "Be ready to start faster",
        titleLevel: 5,
        description:
          "Eligible checks can remove delays once a team moves forward.",
        actionLabel: "Request background check",
        actionLabelLines: ["Request", "background check"],
        icon: "backgroundCheck",
        onAction: () => navigate(paths.backgroundChecks),
      },
      {
        eyebrow: "Application progress",
        title: "Watch for next steps",
        titleLevel: 5,
        description:
          "Track updates, interviews, and hiring notes from this page.",
        icon: "hiring",
      },
    ],
    [navigate],
  );
  const snapshotGroups = useMemo(() => {
    const entries = Array.isArray(application?.professionProfileSnapshot)
      ? application.professionProfileSnapshot
      : [];
    return entries.reduce((groups, item) => {
      const key = item.sectionKey || "profile";
      const group = groups.find((nextGroup) => nextGroup.sectionKey === key);
      if (group) {
        group.items.push(item);
      } else {
        groups.push({
          sectionKey: key,
          sectionLabel: item.sectionLabel || titleCase(key),
          items: [item],
        });
      }
      return groups;
    }, []);
  }, [application]);

  const loadApplication = useCallback(async () => {
    if (!applicationId || !actorId) return;
    setLoading(true);
    setError("");
    try {
      const result = await olreadiaService.jobApplications.get(applicationId, actorId);
      setApplication(result);
    } catch (nextError) {
      setError(nextError?.message || "Could not load application details.");
    } finally {
      setLoading(false);
    }
  }, [actorId, applicationId]);

  useEffect(() => {
    loadApplication();
  }, [loadApplication]);

  useEffect(() => {
    let cancelled = false;
    const loadLinkedJobPost = async () => {
      if (!jobOpportunityId || !actorId) {
        setJobPost(null);
        return;
      }
      if (applicationJobPost?.posterId || applicationJobPost?.poster?.id || jobPost?.id === jobOpportunityId) return;
      try {
        const result = await olreadiaService.jobOpportunities.get(jobOpportunityId, actorId);
        if (!cancelled) setJobPost(result || applicationJobPost || null);
      } catch {
        if (!cancelled && applicationJobPost) setJobPost(applicationJobPost);
      }
    };
    loadLinkedJobPost();
    return () => {
      cancelled = true;
    };
  }, [actorId, applicationJobPost, jobOpportunityId, jobPost?.id]);

  const loadInterviewSessions = useCallback(async () => {
    if (!application?.id || !isInterviewing) {
      setInterviewSessions([]);
      return;
    }
    setLoadingInterviews(true);
    try {
      const result = await olreadiaService.jobApplications.listInterviewSessions(application.id, actorId);
      setInterviewSessions(result);
    } catch {
      setInterviewSessions([]);
    } finally {
      setLoadingInterviews(false);
    }
  }, [actorId, application?.id, isInterviewing]);

  useEffect(() => {
    loadInterviewSessions();
  }, [loadInterviewSessions]);

  const openAction = (action) => {
    setPendingAction(action);
    setActionNote(action.defaultNote || "");
  };

  const openInterviewCreator = () => {
    setInterviewEditor({ session: null });
    interviewForm.setFieldsValue(interviewFormInitialValues(null));
  };

  const openInterviewEditor = (session) => {
    setInterviewEditor({ session });
    interviewForm.setFieldsValue(interviewFormInitialValues(session));
  };

  const closeInterviewEditor = () => {
    setInterviewEditor(null);
    interviewForm.resetFields();
  };

  const submitInterviewSession = async () => {
    if (!application?.id || !interviewEditor) return;
    try {
      const values = await interviewForm.validateFields();
      const payload = interviewSessionPayload(values, interviewSessions, interviewEditor.session);
      setSavingInterview(true);
      if (interviewEditor.session?.id) {
        const updatedSession = await olreadiaService.jobApplications.updateInterviewSession(
          application.id,
          interviewEditor.session.id,
          payload,
          actorId,
        );
        if (interviewDrawerSession?.id === interviewEditor.session.id) {
          setInterviewDrawerSession(updatedSession || { ...interviewEditor.session, ...payload });
        }
        message.success("Interview session updated.");
      } else {
        await olreadiaService.jobApplications.createInterviewSession(application.id, payload, actorId);
        message.success("Interview session added.");
      }
      closeInterviewEditor();
      await loadInterviewSessions();
    } catch (nextError) {
      if (nextError?.errorFields) return;
      message.error(nextError?.message || "Unable to save interview session.");
    } finally {
      setSavingInterview(false);
    }
  };

  const managerActions = useMemo(() => {
    if (!canManageApplication || isClosed || isWithdrawn) return [];
    const updateCurrent = {
      key: "update",
      label: "Give Update",
      icon: <EditOutlined />,
      status: normalizedStatus,
      title: "Update application?",
      message: `This will update the application while keeping the status as ${titleCase(normalizedStatus)}.`,
      confirmLabel: "Update",
      target: "noteToApplicant",
      requireNote: true,
    };
    const moveForward = {
      key: "move-forward",
      label: "Move Forward",
      icon: <SendOutlined />,
      type: "primary",
      status: isInterviewing ? "OFFERED" : "INTERVIEWING",
      title: isInterviewing ? "Move applicant to offer stage?" : "Move applicant forward?",
      message: isInterviewing ? "This will move the applicant to the offer stage." : "This will move the applicant to the next hiring stage.",
      confirmLabel: "Move Forward",
      target: "noteToApplicant",
      defaultNote: isInterviewing
        ? "Congratulations. We are pleased to move you to the offer stage for this opportunity."
        : "Thank you for your application. We are pleased to move you forward to the next stage of our hiring process.",
    };
    const reject = {
      key: "reject",
      label: "Reject Candidate",
      icon: <CloseCircleOutlined />,
      danger: true,
      status: "REJECTED",
      title: "Reject this application?",
      message: "This will mark the applicant as not moving forward for this job.",
      confirmLabel: "Reject",
      target: "noteToApplicant",
      destructive: true,
      defaultNote: "Thank you for your interest. After reviewing your application, we will not be moving forward at this time.",
    };
    const close = {
      key: "close",
      label: closeApplicationActionLabel,
      icon: <StopOutlined />,
      danger: true,
      status: isHiredStatus ? "HIRED_CLOSED" : isRejectedStatus ? "REJECTED_CLOSED" : "CANCELLED_CLOSED",
      title: closeApplicationActionLabel === "Close Application" ? "Close this application?" : "Cancel this application?",
      message: closeApplicationActionLabel === "Close Application"
        ? "This will close the application at its current outcome."
        : "This will close the application as cancelled.",
      confirmLabel: closeApplicationActionLabel,
      target: "noteToApplicant",
      destructive: true,
      defaultNote: isHiredStatus
        ? "This application has been closed because the role was filled."
        : closeApplicationActionLabel === "Close Application"
          ? "This application has been closed for this opportunity."
          : "This application has been cancelled for this opportunity.",
    };
    const hired = {
      key: "hired",
      label: "Hire Candidate",
      icon: <CheckCircleOutlined />,
      type: "primary",
      status: "HIRED",
      title: "Mark applicant as hired?",
      message: "This will mark the applicant as hired for this opportunity.",
      confirmLabel: "Hire Candidate",
      target: "noteToApplicant",
      defaultNote: "Congratulations. We are excited to confirm that you have been selected for this opportunity.",
    };

    const actions = [];
    if (isInitialApplicationStatus) {
      actions.push({
        key: "under-review",
        label: "Under Review",
        icon: <EyeOutlined />,
        status: "UNDER_REVIEW",
        title: "Mark as under review?",
        message: "This will mark the application as currently being reviewed.",
        confirmLabel: "Mark Under Review",
        target: "noteToApplicant",
        defaultNote: "Thank you for your application. Your submission is now under review by the hiring team.",
      });
      actions.push(moveForward);
    } else if (isOfferedStatus) {
      actions.push(updateCurrent, hired);
    } else {
      actions.push(updateCurrent);
      if (!isHiredStatus && !isRejectedStatus) actions.push(moveForward);
    }
    if (!isHiredStatus && !isRejectedStatus) actions.push(reject);
    actions.push(close);
    return actions;
  }, [
    canManageApplication,
    closeApplicationActionLabel,
    isClosed,
    isHiredStatus,
    isInitialApplicationStatus,
    isInterviewing,
    isOfferedStatus,
    isRejectedStatus,
    isWithdrawn,
    normalizedStatus,
  ]);

  const submitAction = async () => {
    if (!application?.id || !pendingAction) return;
    if (pendingAction.requireNote && !actionNote.trim()) {
      message.warning("Please add a message before submitting.");
      return;
    }
    setUpdating(true);
    try {
      const payload = {
        status: pendingAction.status,
        noteToApplicant: pendingAction.target === "noteToApplicant" ? actionNote.trim() || null : null,
        noteToHiringTeam: pendingAction.target === "noteToHiringTeam" ? actionNote.trim() || null : null,
        internalNote: pendingAction.target === "internalNote" ? actionNote.trim() || null : null,
      };
      const updated = await olreadiaService.jobApplications.updateStatus(application.id, payload, actorId);
      setApplication((current) => ({ ...(current || {}), ...(updated || {}), status: updated?.status || pendingAction.status }));
      message.success(pendingAction.success || "Application updated.");
      setPendingAction(null);
      setActionNote("");
    } catch (nextError) {
      message.error(nextError?.message || "Unable to update application.");
    } finally {
      setUpdating(false);
    }
  };

  const shareApplication = async () => {
    const url = `${window.location.origin}${paths.jobApplicationDetails(application?.id || applicationId)}`;
    if (navigator.share) {
      await navigator.share({ title: applicationTitle(application), url });
      return;
    }
    await navigator.clipboard.writeText(url);
    message.success("Application link copied.");
  };

  const openJobPostDrawer = async () => {
    if (!jobOpportunityId && !applicationJobPost) {
      message.warning("No job post is linked to this application.");
      return;
    }

    setJobPost(applicationJobPost || null);
    setJobPostDrawerOpen(true);

    if (!jobOpportunityId || !actorId) return;
    setLoadingJobPost(true);
    try {
      const result = await olreadiaService.jobOpportunities.get(jobOpportunityId, actorId);
      setJobPost(result || applicationJobPost || null);
    } catch {
      message.error("Could not open the original job post.");
    } finally {
      setLoadingJobPost(false);
    }
  };

  if (loading) {
    return (
      <div className="job-application-details-page">
        <Skeleton active />
      </div>
    );
  }

  if (error) {
    return (
      <div className="job-application-details-page">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(paths.jobApplications)} className="job-application-details-back">
          Back to applications
        </Button>
        <Alert type="error" showIcon message="Unable to load application" description={error} action={<Button onClick={loadApplication}>Retry</Button>} />
      </div>
    );
  }

  return (
    <div className="job-application-details-page">
      <div className="job-application-details-layout">
        <main className="job-application-details-main">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(paths.jobApplications)} className="job-application-details-back">
            Back to applications
          </Button>

          <section className="job-application-details-hero">
            <span className="job-application-details-hero__icon"><FileTextOutlined /></span>
            <div className="job-application-details-hero__copy">
              <Tag className={`job-application-details-status is-${statusTone(application?.status)}`}>
                {titleCase(application?.status || "Submitted")}
              </Tag>
              <Title level={2}>{applicationTitle(application)}</Title>
              <Text>Submitted {formatDateTime(application?.createdAt || application?.submittedAt)}</Text>
              {showApplicantActions ? (
                <div className="job-application-details-hero__actions">
                  <Button icon={<MessageOutlined />} onClick={() => openAction({
                    status: normalizedStatus,
                    title: "Request an update?",
                    message: "This will ask the job poster for an update while keeping your current application status.",
                    confirmLabel: "Request Update",
                    target: "noteToHiringTeam",
                    defaultNote: "Hello, I am checking in on my application and would appreciate any updates when available.",
                  })}>
                    Request Update
                  </Button>
                  <Button danger icon={<StopOutlined />} onClick={() => openAction({
                    status: "WITHDRAWN",
                    title: "Withdraw application?",
                    message: "This will withdraw your application from this job opportunity.",
                    confirmLabel: "Withdraw",
                    target: "internalNote",
                    destructive: true,
                    defaultNote: "I would like to withdraw my application for this opportunity. Thank you for your consideration.",
                  })}>
                    Withdraw
                  </Button>
                </div>
              ) : canManageApplication && managerActions.length ? (
                <div className="job-application-details-hero__actions job-application-details-hero__actions--manager">
                  {managerActions.slice(0, 4).map((action) => (
                    <Button
                      key={action.key}
                      type={action.type}
                      danger={action.danger}
                      icon={action.icon}
                      onClick={() => openAction(action)}
                    >
                      {action.label}
                    </Button>
                  ))}
                </div>
              ) : null}
            </div>
          </section>

          <Collapse
            defaultActiveKey={[]}
            className="job-application-details-collapse"
            items={[
              {
                key: "overview",
                label: "Overview",
                children: (
                  <div className="job-application-details-overview-compact">
                    <div className="job-application-details-overview-main">
                      <span>
                        <Text type="secondary">Status</Text>
                        <strong>{titleCase(application?.status || "Submitted")}</strong>
                      </span>
                      <span>
                        <Text type="secondary">Submitted</Text>
                        <strong>{formatDateTime(application?.createdAt || application?.submittedAt)}</strong>
                      </span>
                      <span>
                        <Text type="secondary">Updated</Text>
                        <strong>{formatDateTime(application?.updatedAt)}</strong>
                      </span>
                    </div>
                    <div className="job-application-details-overview-meta">
                      <Tag color={application?.shareProfile ? "green" : "default"}>Profile: {sharedLabel(application?.shareProfile)}</Tag>
                      <Tag color={application?.shareBackgroundCheck ? "blue" : "default"}>Background Check: {sharedLabel(application?.shareBackgroundCheck)}</Tag>
                      {jobOpportunityId || applicationJobPost ? (
                        <Button
                          type="link"
                          className="job-application-details-job-link"
                          onClick={openJobPostDrawer}
                        >
                          Open original job post
                        </Button>
                      ) : (
                        <Tag>Job Opportunity: Not set</Tag>
                      )}
                    </div>
                  </div>
                ),
              },
              history.length
                ? {
                    key: "history",
                    label: "Status History",
                    children: (
                      <div className="job-application-details-history">
                        {history.map((item, index) => (
                          <Card key={`${item.status || "status"}-${item.addedAt || index}`} className="job-application-details-history-card">
                            <div className="job-application-details-history-card__top">
                              <Tag>{titleCase(item.status || "Updated")}</Tag>
                              <Text type="secondary">{formatDateTime(item.addedAt || item.createdAt)}</Text>
                            </div>
                            <HistoryNote label="Applicant note" value={item.noteToApplicant} />
                            <HistoryNote label="Hiring team note" value={item.noteToHiringTeam || item.note} />
                            {canManageApplication ? <HistoryNote label="Internal note" value={item.internalNote} /> : null}
                          </Card>
                        ))}
                      </div>
                    ),
                  }
                : null,
              isInterviewing
                ? {
                    key: "interviews",
                    label: "Interview Sessions",
                    children: (
                      <div className="job-application-details-interview-section">
                        <div className="job-application-details-interview-toolbar">
                          <div>
                            <Text className="job-application-details-interview-toolbar__title">
                              {interviewSessions.length
                                ? `${interviewSessions.length} session${interviewSessions.length === 1 ? "" : "s"}`
                                : "No scheduled sessions"}
                            </Text>
                            <Text type="secondary">Manage interview steps for this application.</Text>
                          </div>
                          {canManageApplication ? (
                            <Button
                              type="primary"
                              icon={<CalendarOutlined />}
                              onClick={openInterviewCreator}
                            >
                              Add Interview Session
                            </Button>
                          ) : null}
                        </div>

                        {loadingInterviews ? (
                          <Skeleton active />
                        ) : interviewSessions.length ? (
                          <List
                            dataSource={[...interviewSessions].sort((left, right) => {
                              const byOrder = (numericValue(left.orderNumber) || 999) - (numericValue(right.orderNumber) || 999);
                              if (byOrder !== 0) return byOrder;
                              return sessionTimeValue(left) - sessionTimeValue(right);
                            })}
                            renderItem={(session) => (
                              <List.Item>
                                <Card className="job-application-details-interview-card">
                                  <div className="job-application-details-interview-card__top">
                                    <Space align="start">
                                      <span className="job-application-details-interview-card__icon">
                                        <CalendarOutlined />
                                      </span>
                                      <div className="job-application-details-interview-card__content">
                                        <Text strong>{session.title || "Interview session"}</Text>
                                        <div className="job-application-details-interview-card__meta">
                                          <span>Date: {formatDateTime(session.interviewDateTime)}</span>
                                          <span>Mode: {titleCase(session.interviewMode || session.type) || "Not set"}</span>
                                          <span>Status: {titleCase(session.status || "Scheduled")}</span>
                                          {session.durationMinutes ? <span>Duration: {session.durationMinutes} min</span> : null}
                                          {session.location ? <span>Location: {session.location}</span> : null}
                                        </div>
                                      </div>
                                    </Space>
                                    <Space size={6} className="job-application-details-interview-card__actions">
                                      <Button
                                        size="small"
                                        icon={<EyeOutlined />}
                                        onClick={() => setInterviewDrawerSession(session)}
                                      >
                                        View
                                      </Button>
                                      {canManageApplication ? (
                                        <Button
                                          size="small"
                                          icon={<EditOutlined />}
                                          onClick={() => openInterviewEditor(session)}
                                        >
                                          Edit
                                        </Button>
                                      ) : null}
                                    </Space>
                                  </div>
                                  {session.meetingLink || session.notesToCandidate || asList(session.interviewers).length ? (
                                    <div className="job-application-details-interview-card__extra">
                                      {session.meetingLink ? (
                                        <Button type="link" href={session.meetingLink} target="_blank" rel="noreferrer">
                                          Meeting link
                                        </Button>
                                      ) : null}
                                      {asList(session.interviewers).length ? (
                                        <Text type="secondary">Interviewers: {interviewersInputValue(session.interviewers)}</Text>
                                      ) : null}
                                      {session.notesToCandidate ? <Text type="secondary">{session.notesToCandidate}</Text> : null}
                                    </div>
                                  ) : null}
                                </Card>
                              </List.Item>
                            )}
                          />
                        ) : (
                          <Empty
                            description="No interview sessions have been scheduled yet."
                          >
                            {canManageApplication ? (
                              <Button type="primary" icon={<CalendarOutlined />} onClick={openInterviewCreator}>
                                Add Interview Session
                              </Button>
                            ) : null}
                          </Empty>
                        )}
                      </div>
                    ),
                  }
                : null,
              (noteParts.intro || (application?.note && !noteParts.answers.length))
                ? {
                    key: "note",
                    label: "Application Note",
                    children: (
                      <>
                        {noteParts.intro ? <Paragraph className="job-application-details-note">{noteParts.intro}</Paragraph> : null}
                        {!noteParts.answers.length && !noteParts.intro ? (
                          <Paragraph className="job-application-details-note">{application.note}</Paragraph>
                        ) : null}
                      </>
                    ),
                  }
                : null,
              {
                key: "snapshot",
                label: "Profile Snapshot",
                children: snapshotGroups.length ? (
                  <div className="job-application-details-snapshot">
                    {snapshotGroups.map((group) => (
                      <Card key={group.sectionKey} className="job-application-details-snapshot-group">
                        <Text className="job-application-details-snapshot-group__title">{group.sectionLabel}</Text>
                        {group.items.map((item, index) => {
                          const attachment = snapshotAttachmentDisplay(item, index);
                          return (
                            <div key={`${item.sectionKey}-${item.key}`} className="job-application-details-snapshot-item">
                              <Text type="secondary">{item.label}</Text>
                              {attachment ? (
                                <div className="job-application-details-snapshot-file">
                                  <span className="job-application-details-snapshot-file__icon"><PaperClipOutlined /></span>
                                  <div>
                                    <strong>{attachment.title}</strong>
                                    {attachment.subtitle ? <span>{attachment.subtitle}</span> : null}
                                    {attachment.meta ? <Text type="secondary">{attachment.meta}</Text> : null}
                                  </div>
                                  <Button
                                    size="small"
                                    icon={<EyeOutlined />}
                                    disabled={!attachment.fileUri}
                                    onClick={() => setAttachmentPreview(attachment)}
                                  >
                                    View
                                  </Button>
                                </div>
                              ) : (
                                <strong>{displaySnapshotValue(item.value)}</strong>
                              )}
                            </div>
                          );
                        })}
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Empty description="No profile snapshot was shared." />
                ),
              },
              applicationQuestionAnswers.length
                ? {
                    key: "questions",
                    label: "Application Questions",
                    children: <QuestionAnswerList answers={applicationQuestionAnswers} />,
                  }
                : null,
            ].filter(Boolean)}
          />
        </main>

        <aside className="job-application-details-side">
          <Card className="job-application-details-side-card">
            <Space direction="vertical" size={10} className="job-application-details-side-card__stack">
              <Button
                type="link"
                icon={<EyeOutlined />}
                className="job-application-details-applicant"
                disabled={!application?.professionProfileId}
                onClick={() => navigate(paths.professionalProfiles)}
              >
                {applicantName(application)}
              </Button>
              <Button icon={<ShareAltOutlined />} onClick={shareApplication}>
                Share
              </Button>
            </Space>
          </Card>

          {canManageApplication ? (
            <Card className="job-application-details-actions">
              {managerActions.length ? managerActions.map((action) => (
                <Button
                  key={action.key}
                  type={action.type}
                  danger={action.danger}
                  icon={action.icon}
                  onClick={() => openAction(action)}
                >
                  {action.label}
                </Button>
              )) : (
                <Text type="secondary">No application management actions are available for this status.</Text>
              )}
            </Card>
          ) : null}

          <Card className="job-application-details-side-card job-application-details-job-card">
            <Text className="job-application-details-context__label">Job post</Text>
            <strong className="job-application-details-side-card__title">
              {applicationTitle(application)}
            </strong>
            <Button
              type="primary"
              icon={<ProfileOutlined />}
              onClick={openJobPostDrawer}
              disabled={!jobOpportunityId && !applicationJobPost}
            >
              Job Post Details
            </Button>
          </Card>

          <Card className="job-application-details-side-card">
            <Text className="job-application-details-context__label">Workspace context</Text>
            <strong className="job-application-details-side-card__title">{selectedWorkspace?.name || "Workspace"}</strong>
            <Tag color={selectedWorkspaceUse === "HIRING" ? "blue" : "green"}>
              {selectedWorkspaceUse === "HIRING" ? "Hiring" : "My Job Search"}
            </Tag>
          </Card>
        </aside>

        <SidePromotionSection className="job-application-details-promotions" items={promotionItems} />
      </div>

      <ActionModal
        action={pendingAction}
        value={actionNote}
        setValue={setActionNote}
        loading={updating}
        onCancel={() => setPendingAction(null)}
        onSubmit={submitAction}
      />
      <Modal
        open={Boolean(interviewEditor)}
        title={
          <span className="job-application-action-modal__title">
            {interviewEditor?.session ? "Edit Interview Session" : "Add Interview Session"}
          </span>
        }
        className="job-application-interview-modal"
        okText={interviewEditor?.session ? "Save Session" : "Add Session"}
        okButtonProps={{
          className: "job-application-action-modal__ok",
          loading: savingInterview,
        }}
        onCancel={closeInterviewEditor}
        onOk={submitInterviewSession}
        width="min(720px, 100vw)"
        destroyOnClose
      >
        <Form
          form={interviewForm}
          layout="vertical"
          className="job-application-interview-form"
          initialValues={interviewFormInitialValues(interviewEditor?.session)}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Add an interview title." }]}
          >
            <Input placeholder="Technical interview" />
          </Form.Item>

          <div className="job-application-interview-form__grid">
            <Form.Item
              name="type"
              label="Type"
              rules={[{ required: true, message: "Select a session type." }]}
            >
              <Select options={selectOptions(INTERVIEW_TYPE_OPTIONS)} />
            </Form.Item>
            <Form.Item
              name="interviewMode"
              label="Mode"
              rules={[{ required: true, message: "Select an interview mode." }]}
            >
              <Select options={selectOptions(INTERVIEW_MODE_OPTIONS)} />
            </Form.Item>
          </div>

          <div className="job-application-interview-form__grid">
            <Form.Item name="interviewDateTime" label="Date and time">
              <Input type="datetime-local" />
            </Form.Item>
            <Form.Item name="durationMinutes" label="Duration">
              <InputNumber min={1} max={600} addonAfter="min" className="job-application-interview-form__number" />
            </Form.Item>
          </div>

          <div className="job-application-interview-form__grid">
            <Form.Item name="status" label="Status">
              <Select options={selectOptions(INTERVIEW_STATUS_OPTIONS)} />
            </Form.Item>
            <Form.Item name="interviewFeedbackStatus" label="Feedback">
              <Select options={selectOptions(INTERVIEW_FEEDBACK_OPTIONS)} />
            </Form.Item>
          </div>

          <div className="job-application-interview-form__grid">
            <Form.Item name="meetingLink" label="Meeting link">
              <Input placeholder="https://..." />
            </Form.Item>
            <Form.Item name="location" label="Location">
              <Input placeholder="Office, room, or address" />
            </Form.Item>
          </div>

          <Form.Item name="interviewers" label="Interviewers">
            <Input placeholder="Names separated by commas" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Internal session details" />
          </Form.Item>

          <Form.Item name="notesToCandidate" label="Notes to candidate">
            <TextArea rows={3} placeholder="What the candidate should prepare or know" />
          </Form.Item>

          <div className="job-application-interview-form__grid">
            <Form.Item name="interviewFeedbackToCandidate" label="Candidate feedback">
              <TextArea rows={3} />
            </Form.Item>
            <Form.Item name="interviewFeedbackInternal" label="Internal feedback">
              <TextArea rows={3} />
            </Form.Item>
          </div>
        </Form>
      </Modal>
      <Drawer
        open={Boolean(interviewDrawerSession)}
        onClose={() => setInterviewDrawerSession(null)}
        title={interviewDrawerSession?.title || "Interview Session"}
        width="min(500px, 100vw)"
        className="job-application-interview-drawer"
      >
        {interviewDrawerSession ? (
          <div className="job-application-interview-drawer__content">
            <div className="job-application-interview-drawer__hero">
              <span className="job-application-details-interview-card__icon">
                <CalendarOutlined />
              </span>
              <div>
                <Text type="secondary">Interview session</Text>
                <Title level={4}>{interviewDrawerSession.title || "Interview session"}</Title>
                <div className="job-application-interview-drawer__status">
                  <Tag color={String(interviewDrawerSession.status || "").toUpperCase() === "CANCELLED" ? "red" : "green"}>
                    {titleCase(interviewDrawerSession.status || "Scheduled")}
                  </Tag>
                </div>
              </div>
            </div>

            <div className="job-application-interview-drawer__tags">
              <Tag>{formatDateTime(interviewDrawerSession.interviewDateTime)}</Tag>
              {interviewDrawerSession.type ? <Tag>{titleCase(interviewDrawerSession.type)}</Tag> : null}
              {interviewDrawerSession.interviewMode ? <Tag>{titleCase(interviewDrawerSession.interviewMode)}</Tag> : null}
              {formatDuration(interviewDrawerSession.durationMinutes ?? interviewDrawerSession.duration) ? (
                <Tag>{formatDuration(interviewDrawerSession.durationMinutes ?? interviewDrawerSession.duration)}</Tag>
              ) : null}
              <Tag>{interviewFeedbackLabel(interviewDrawerSession.interviewFeedbackStatus)}</Tag>
            </div>

            <div className="job-application-interview-drawer__actions">
              {interviewDrawerSession.meetingLink ? (
                <Button
                  type="primary"
                  href={interviewDrawerSession.meetingLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Meeting Link
                </Button>
              ) : null}
              {canManageApplication ? (
                <Button
                  icon={<EditOutlined />}
                  onClick={() => openInterviewEditor(interviewDrawerSession)}
                >
                  Edit Session
                </Button>
              ) : null}
            </div>

            <div className="job-application-interview-drawer__details">
              {interviewSessionDetailItems(interviewDrawerSession).map((item) => (
                <div
                  key={item.label}
                  className={item.link ? "job-application-interview-drawer__detail--wide" : undefined}
                >
                  <Text type="secondary">{item.label}</Text>
                  {item.link ? (
                    <Button type="link" href={item.value} target="_blank" rel="noreferrer">
                      {item.value}
                    </Button>
                  ) : (
                    <span>{item.value}</span>
                  )}
                </div>
              ))}
            </div>

            {interviewSessionTextSections(interviewDrawerSession, canManageApplication).map((section) => (
              <section key={section.label} className="job-application-interview-drawer__section">
                <Text>{section.label}</Text>
                <Paragraph>{section.value}</Paragraph>
              </section>
            ))}
          </div>
        ) : null}
      </Drawer>
      <JobPostDrawer
        job={jobPost || applicationJobPost}
        open={jobPostDrawerOpen}
        loading={loadingJobPost}
        onClose={() => setJobPostDrawerOpen(false)}
      />
      <Modal
        open={Boolean(attachmentPreview)}
        title={attachmentPreview?.title || "Attachment Preview"}
        footer={attachmentPreview?.fileUri ? (
          <Button type="primary" href={attachmentPreview.fileUri} target="_blank" rel="noreferrer">
            Open in new tab
          </Button>
        ) : null}
        onCancel={() => setAttachmentPreview(null)}
        width="min(920px, 100vw)"
        destroyOnClose
      >
        {attachmentPreview?.fileUri ? (
          <iframe
            className="job-application-details-attachment-preview"
            title={attachmentPreview.title || "Attachment Preview"}
            src={attachmentPreview.fileUri}
          />
        ) : (
          <Empty description="Preview unavailable" />
        )}
      </Modal>
    </div>
  );
}
